import React, { useState, useMemo } from 'react';
import { format, startOfMonth, addMonths, subMonths, isToday, isSameDay } from 'date-fns';
import { Button } from '@vehix/portal/components/ui/button';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@vehix/portal/components/ui/card';
import { ScrollArea } from '@vehix/portal/components/ui/scroll-area';
import {
  ChevronLeft,
  ChevronRight,
  Play,
  Loader2,
  Clock,
  Calendar,
  CalendarDays
} from 'lucide-react';
import { cn } from '@vehix/portal/lib/utils';
import { Schedule, ScheduleWithRunningStatus } from '@vehix/portal/services/api/scheduling';
import { groupSchedulesByDay } from '@vehix/shared/admin/utils/cronCalendar';
import { parseScheduleName } from '@vehix/portal/utils/scheduleNameParser';

interface SchedulesCalendarViewProps {
  schedules: (ScheduleWithRunningStatus & { _fromHistory?: boolean })[];
  onTrigger: (schedule: Schedule) => void;
  onScheduleClick: (schedule: Schedule) => void;
  isTriggering?: boolean;
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Format hour to display label (e.g., 6 -> "6 AM", 14 -> "2 PM")
const formatHourLabel = (hour: number): string => {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
};

export const SchedulesCalendarView: React.FC<SchedulesCalendarViewProps> = ({
  schedules,
  onTrigger,
  onScheduleClick,
  isTriggering
}) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

  // Group schedules by day for the current month
  const schedulesByDay = useMemo(() => {
    return groupSchedulesByDay(schedules, currentMonth);
  }, [schedules, currentMonth]);

  // Get schedules for the selected date, sorted by time
  const selectedDateSchedules = useMemo(() => {
    if (!selectedDate) return [];
    const dateKey = format(selectedDate, 'yyyy-MM-dd');
    const daySchedules = schedulesByDay.get(dateKey) || [];
    return daySchedules.sort((a, b) => {
      if (a.hour !== b.hour) return a.hour - b.hour;
      return a.minute - b.minute;
    });
  }, [selectedDate, schedulesByDay]);

  // Calculate dynamic timeline range based on scheduled jobs (30 min buffer)
  const timelineRange = useMemo(() => {
    if (selectedDateSchedules.length === 0) {
      return { startHour: 6, startMinute: 0, endHour: 18, endMinute: 0 };
    }

    // Find earliest and latest scheduled times
    const firstJob = selectedDateSchedules[0];
    const lastJob = selectedDateSchedules[selectedDateSchedules.length - 1];

    // Calculate start: 30 min before first job
    let startMinutes = firstJob.hour * 60 + firstJob.minute - 30;
    startMinutes = Math.max(0, startMinutes); // Don't go below midnight
    const startHour = Math.floor(startMinutes / 60);
    const startMinute = startMinutes % 60;

    // Calculate end: 30 min after last job
    let endMinutes = lastJob.hour * 60 + lastJob.minute + 30;
    endMinutes = Math.min(24 * 60 - 1, endMinutes); // Don't go past midnight
    const endHour = Math.floor(endMinutes / 60);
    const endMinute = endMinutes % 60;

    return { startHour, startMinute, endHour, endMinute };
  }, [selectedDateSchedules]);

  // Generate dynamic time slots for the timeline markers
  const timeSlots = useMemo(() => {
    const { startHour, endHour } = timelineRange;
    const slots: { hour: number; label: string }[] = [];

    // Add start hour
    slots.push({ hour: startHour, label: formatHourLabel(startHour) });

    // Add intermediate hours (every 1-3 hours depending on range)
    const range = endHour - startHour;
    const step = range <= 4 ? 1 : range <= 8 ? 2 : 3;
    for (let h = startHour + step; h < endHour; h += step) {
      slots.push({ hour: h, label: formatHourLabel(h) });
    }

    // Add end hour if different from last slot
    if (slots[slots.length - 1].hour !== endHour) {
      slots.push({ hour: endHour, label: formatHourLabel(endHour) });
    }

    return slots;
  }, [timelineRange]);

  // Navigation handlers
  const goToPreviousMonth = () => setCurrentMonth(prev => subMonths(prev, 1));
  const goToNextMonth = () => setCurrentMonth(prev => addMonths(prev, 1));
  const goToToday = () => {
    setCurrentMonth(startOfMonth(new Date()));
    setSelectedDate(new Date());
  };

  // Generate calendar days for current month view (compact - 5 rows max)
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const startDay = start.getDay();

    const prevMonthDays: Date[] = [];
    for (let i = startDay - 1; i >= 0; i--) {
      const date = new Date(start);
      date.setDate(date.getDate() - (i + 1));
      prevMonthDays.push(date);
    }

    const daysInMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    ).getDate();

    const currentMonthDays: Date[] = [];
    for (let i = 1; i <= daysInMonth; i++) {
      currentMonthDays.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
    }

    const totalDays = prevMonthDays.length + currentMonthDays.length;
    const nextMonthDays: Date[] = [];
    const rows = Math.ceil(totalDays / 7);
    const daysToAdd = rows * 7 - totalDays;
    for (let i = 1; i <= daysToAdd; i++) {
      nextMonthDays.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, i));
    }

    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  }, [currentMonth]);

  const isCurrentMonth = (date: Date) => date.getMonth() === currentMonth.getMonth();

  const getDateScheduleInfo = (date: Date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    const daySchedules = schedulesByDay.get(dateKey) || [];
    const hasRunning = daySchedules.some(
      occ => schedules.find(s => s.id === occ.schedule.id)?.is_running
    );
    return { count: daySchedules.length, hasRunning };
  };

  const getScheduleWithStatus = (scheduleId: string) => {
    return schedules.find(s => s.id === scheduleId);
  };

  // Get position for timeline item (percentage from top) using dynamic range
  const getTimelinePosition = (hour: number, minute: number) => {
    const { startHour, startMinute, endHour, endMinute } = timelineRange;
    const rangeStartMinutes = startHour * 60 + startMinute;
    const rangeEndMinutes = endHour * 60 + endMinute;
    const totalMinutes = rangeEndMinutes - rangeStartMinutes;
    const itemMinutes = hour * 60 + minute - rangeStartMinutes;
    return Math.max(0, Math.min(100, (itemMinutes / totalMinutes) * 100));
  };

  return (
    <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
      {/* Calendar - Left Side (Compact) */}
      <div className='lg:col-span-1'>
        <Card>
          <CardHeader className='pb-2 pt-4 px-4'>
            <div className='flex items-center justify-between'>
              <Button variant='ghost' size='icon' className='h-7 w-7' onClick={goToPreviousMonth}>
                <ChevronLeft className='h-4 w-4' />
              </Button>
              <CardTitle className='text-sm font-semibold'>
                {format(currentMonth, 'MMMM yyyy')}
              </CardTitle>
              <Button variant='ghost' size='icon' className='h-7 w-7' onClick={goToNextMonth}>
                <ChevronRight className='h-4 w-4' />
              </Button>
            </div>
          </CardHeader>
          <CardContent className='px-3 pb-3'>
            {/* Weekday Headers */}
            <div className='grid grid-cols-7 mb-1'>
              {WEEKDAYS.map((day, idx) => (
                <div
                  key={`weekday-${idx}`}
                  className='h-7 flex items-center justify-center text-[10px] font-medium text-muted-foreground'
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days - Compact */}
            <div className='grid grid-cols-7'>
              {calendarDays.map(date => {
                const { count, hasRunning } = getDateScheduleInfo(date);
                const isSelected = selectedDate && isSameDay(date, selectedDate);
                const isTodayDate = isToday(date);
                const isOutside = !isCurrentMonth(date);
                const dateKey = format(date, 'yyyy-MM-dd');

                return (
                  <button
                    key={dateKey}
                    onClick={() => setSelectedDate(date)}
                    className={cn(
                      'relative h-8 w-full flex items-center justify-center text-xs rounded transition-colors',
                      'hover:bg-accent hover:text-accent-foreground',
                      'focus:outline-none focus:ring-1 focus:ring-ring',
                      isOutside && 'text-muted-foreground/30',
                      isTodayDate && !isSelected && 'bg-accent font-bold',
                      isSelected && 'bg-primary text-primary-foreground font-bold',
                      count > 0 && !isOutside && !isSelected && 'font-semibold'
                    )}
                  >
                    {format(date, 'd')}
                    {count > 0 && !isOutside && (
                      <span
                        className={cn(
                          'absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full',
                          isSelected
                            ? 'bg-primary-foreground'
                            : hasRunning
                              ? 'bg-blue-500 animate-pulse'
                              : 'bg-primary'
                        )}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Today Button */}
            <div className='mt-3 flex justify-center'>
              <Button variant='ghost' size='sm' className='text-xs h-7' onClick={goToToday}>
                Today
              </Button>
            </div>

            {/* Legend */}
            <div className='mt-3 pt-3 border-t flex items-center justify-center gap-4 text-[10px] text-muted-foreground'>
              <div className='flex items-center gap-1'>
                <div className='w-1.5 h-1.5 rounded-full bg-primary' />
                <span>Scheduled</span>
              </div>
              <div className='flex items-center gap-1'>
                <div className='w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse' />
                <span>Running</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timeline View - Right Side */}
      <div className='lg:col-span-2'>
        <Card className='h-full'>
          <CardHeader className='pb-2'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <CalendarDays className='h-5 w-5 text-muted-foreground' />
                <CardTitle className='text-base'>
                  {selectedDate ? format(selectedDate, 'EEEE, MMMM d') : 'Select a Date'}
                </CardTitle>
              </div>
              {selectedDateSchedules.length > 0 && (
                <Badge variant='secondary'>
                  {selectedDateSchedules.length} schedule
                  {selectedDateSchedules.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className='pt-0'>
            {!selectedDate ? (
              <div className='flex flex-col items-center justify-center py-12 text-center'>
                <Calendar className='h-12 w-12 text-muted-foreground/50 mb-3' />
                <p className='text-sm text-muted-foreground'>
                  Click on a date to view scheduled jobs
                </p>
              </div>
            ) : selectedDateSchedules.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-12 text-center'>
                <Calendar className='h-12 w-12 text-muted-foreground/50 mb-3' />
                <p className='text-sm text-muted-foreground'>No schedules for this date</p>
              </div>
            ) : (
              <ScrollArea className='h-[450px]'>
                {/* Timeline Container */}
                <div className='relative flex'>
                  {/* Time Labels Column */}
                  <div className='w-16 flex-shrink-0 relative'>
                    {timeSlots.map(slot => (
                      <div
                        key={`time-label-${slot.hour}`}
                        className='absolute text-[10px] text-muted-foreground -translate-y-1/2'
                        style={{ top: `${getTimelinePosition(slot.hour, 0)}%` }}
                      >
                        {slot.label}
                      </div>
                    ))}
                  </div>

                  {/* Timeline Track */}
                  <div className='flex-1 relative min-h-[400px]'>
                    {/* Vertical line */}
                    <div className='absolute left-0 top-0 bottom-0 w-px bg-border' />

                    {/* Hour markers */}
                    {timeSlots.map(slot => (
                      <div
                        key={`time-marker-${slot.hour}`}
                        className='absolute left-0 w-3 h-px bg-border'
                        style={{ top: `${getTimelinePosition(slot.hour, 0)}%` }}
                      />
                    ))}

                    {/* Schedule Items */}
                    {selectedDateSchedules.map(occurrence => {
                      const fullSchedule = getScheduleWithStatus(occurrence.schedule.id);
                      const isRunning = fullSchedule?.is_running;
                      const isEnabled = occurrence.schedule.is_enabled;
                      const displayName = parseScheduleName(occurrence.schedule.schedule_name);
                      const position = getTimelinePosition(occurrence.hour, occurrence.minute);

                      return (
                        <div
                          key={occurrence.schedule.id}
                          className='absolute left-4 right-0'
                          style={{ top: `${position}%` }}
                        >
                          <div
                            className={cn(
                              'p-3 rounded-lg border bg-card transition-all',
                              'hover:bg-muted/50 hover:shadow-sm cursor-pointer',
                              !isEnabled && 'opacity-60',
                              isRunning && 'ring-2 ring-blue-400 bg-blue-50/50 dark:bg-blue-950/20'
                            )}
                            role='button'
                            tabIndex={0}
                            onClick={() => onScheduleClick(occurrence.schedule)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onScheduleClick(occurrence.schedule);
                              }
                            }}
                          >
                            {/* Time marker dot */}
                            <div
                              className={cn(
                                'absolute -left-[18px] top-4 w-2 h-2 rounded-full border-2 border-background',
                                isRunning ? 'bg-blue-500' : 'bg-primary'
                              )}
                            />

                            <div className='flex items-start justify-between gap-3'>
                              <div className='flex-1 min-w-0'>
                                {/* Time */}
                                <div className='flex items-center gap-2 mb-1'>
                                  <Clock className='h-3 w-3 text-muted-foreground' />
                                  <span className='text-sm font-medium'>{occurrence.time}</span>
                                  {isRunning && (
                                    <Badge
                                      variant='outline'
                                      className='bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 text-xs py-0'
                                    >
                                      <Loader2 className='h-2.5 w-2.5 mr-1 animate-spin' />
                                      Running
                                    </Badge>
                                  )}
                                  {!isEnabled && !isRunning && (
                                    <Badge variant='secondary' className='text-xs py-0'>
                                      Disabled
                                    </Badge>
                                  )}
                                </div>

                                {/* Schedule Name */}
                                <h4 className='font-medium text-sm'>{displayName}</h4>
                              </div>

                              {/* Trigger Button */}
                              <Button
                                variant='outline'
                                size='sm'
                                className='h-7 px-2 flex-shrink-0'
                                onClick={e => {
                                  e.stopPropagation();
                                  onTrigger(occurrence.schedule);
                                }}
                                disabled={isTriggering || !isEnabled || isRunning}
                              >
                                {isRunning ? (
                                  <Loader2 className='h-3 w-3 animate-spin' />
                                ) : (
                                  <Play className='h-3 w-3' />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SchedulesCalendarView;
